/**
 * Curtains the app's way: a catalog model tagged `Curtains` hung on the wall over a window, a door or an
 * arch (`AddCurtains` opens the list, `ReplaceCurtains` hangs the one picked), which the opening finds
 * again only by where it hangs (`OpeningFurnitureController.GetCurrentCurtains`): nothing in the project
 * ties the two, so a project from the app and one from the editor read alike.
 *
 * Everything here takes a level and returns the next one, so the store commits each call as one undo
 * step and `tools/test-curtains.ts` calls them directly.
 */
import type { Item, Level, ModelRef, Opening, OpeningDesign, Wall } from '../core/hp/types';
import { openingKind } from './openingProperties';
import { cachedModelTags } from './modelTags';

/** A model's catalog tags: a window from the cloud is only a window by its tags. */
export type TagsOf = (modelId: string) => readonly string[];
/** The tags the editor has already read (`knownModelTags`), without asking the catalog from a level edit. */
const knownTags: TagsOf = id => cachedModelTags(id) ?? [];

/** `FurnitureInfo.IsCurtains`. */
export const isCurtain = (item: Pick<Item, 'tags'>): boolean => !!item.tags?.includes('Curtains');
/** Drapes of the ordinary kind (tags Half or Full) hang wider than the opening; a blind or a roller as wide as it. */
export const isUsualCurtain = (tags: readonly string[] = []): boolean => tags.includes('Curtains') && (tags.includes('Half') || tags.includes('Full'));
/** `UpdateCurtainSizeByOpening`: drapes are 1.2 times the opening's width. */
export const CURTAIN_WIDTH_FACTOR = 1.2;
/** `UpdateCurtainPosByOpening`'s alignmentShift: the curtain's back hangs this far off the wall. */
export const CURTAIN_GAP = .022;

/** `FurnitureInfo.CanAddCurtains`: a window, a door or an arch; a plain gap takes none. */
export function takesCurtains(opening: Opening, tags: readonly string[] = []): boolean {
  const kind = openingKind(opening, tags);
  return kind === 'window' || kind === 'door' || kind === 'arch';
}

/** A rectangle on the wall's face: along the wall from its start, and up from the floor. */
interface Span { a0: number; a1: number; y0: number; y1: number }
const area = (span: Span) => Math.max(0, span.a1 - span.a0) * Math.max(0, span.y1 - span.y0);
const overlap = (a: Span, b: Span) => area({ a0: Math.max(a.a0, b.a0), a1: Math.min(a.a1, b.a1), y0: Math.max(a.y0, b.y0), y1: Math.min(a.y1, b.y1) });
const openingSpan = (opening: Opening): Span => ({ a0: opening.offset - opening.width / 2, a1: opening.offset + opening.width / 2, y0: opening.sill, y1: opening.sill + opening.height });
const alongWall = (wall: Wall, item: Item) => (item.position.x - wall.start.x) * wall.dir.x + (item.position.z - wall.start.z) * wall.dir.z;
const itemSpan = (wall: Wall, item: Item): Span => {
  const along = alongWall(wall, item);
  return { a0: along - item.size.w / 2, a1: along + item.size.w / 2, y0: item.elevation, y1: item.elevation + item.size.h };
};

/**
 * Whether `item` hangs on the room's face of `wall`, where a curtain of its openings goes: the wall it names, on
 * the room's side, or, for one that names none, a back within a few centimetres of that face along the wall.
 */
function hangsOn(wall: Wall, item: Item): boolean {
  if (item.placement !== 'wall') return false;
  const dx = item.position.x - wall.start.x, dz = item.position.z - wall.start.z;
  const across = dx * wall.normalIn.x + dz * wall.normalIn.z;
  if (item.wallId === wall.id) return across > 0;
  const back = across - item.size.d / 2 - wall.thickness / 2, along = dx * wall.dir.x + dz * wall.dir.z;
  return !item.wallId && back > -.05 && back < .15 && along > -item.size.w / 2 && along < wall.length + item.size.w / 2;
}

/**
 * `GetCurrentCurtains`: the curtains on the opening's wall that cover it and are its own. A curtain another window
 * of the wall covers as much of, or more, is that window's; one that covers two windows whole (89 % of each for
 * drapes, 98 % for the rest) is neither's. Largest share of the curtain first is not promised: the project's order.
 */
export function openingCurtains(level: Pick<Level, 'walls' | 'openings' | 'items'>, opening: Opening, tagsOf: TagsOf = knownTags): Item[] {
  const wall = level.walls.find(value => value.id === opening.wallId);
  const own = openingSpan(opening), ownArea = area(own);
  if (!wall || !(ownArea > 0)) return [];
  const windows = level.openings.filter(other => other.id !== opening.id && other.wallId === wall.id
    && openingKind(other, tagsOf(other.modelRef.id)) === 'window');
  const result: Item[] = [];
  for (const item of level.items) {
    if (!isCurtain(item) || !hangsOn(wall, item)) continue;
    const curtain = itemSpan(wall, item), curtainArea = area(curtain), shared = overlap(curtain, own);
    if (!(shared > 0) || !(curtainArea > 0)) continue;
    const usual = isUsualCurtain(item.tags), mine = shared / curtainArea * 100, covered = shared / ownArea * 100;
    let whole = (usual && covered > 89) || covered > 98 ? 1 : 0, belongs = true;
    for (const other of windows) {
      const span = openingSpan(other), otherArea = area(span);
      if (!(otherArea > 0)) continue;
      const common = overlap(curtain, span), theirs = common / curtainArea * 100, coveredThere = common / otherArea * 100;
      if ((usual && coveredThere > 89) || coveredThere > 98) whole++;
      if (whole === 2) break;
      if (theirs < mine) continue;
      belongs = false;
      break;
    }
    if (belongs && whole < 2) result.push(item);
  }
  return result;
}

/** `GetCurrentCurtain`: of the opening's curtains, the one it covers most of. */
export function openingCurtain(level: Pick<Level, 'walls' | 'openings' | 'items'>, opening: Opening, tagsOf: TagsOf = knownTags): Item | undefined {
  const wall = level.walls.find(value => value.id === opening.wallId);
  if (!wall) return undefined;
  const own = openingSpan(opening);
  let best: Item | undefined, share = -1;
  for (const item of openingCurtains(level, opening, tagsOf)) {
    const span = itemSpan(wall, item), value = overlap(span, own) / Math.max(area(span), 1e-9);
    if (value > share) { best = item; share = value; }
  }
  return best;
}

/** What a curtain is made from: a catalog card, or a curtain already hanging. */
export interface CurtainModel { modelRef: ModelRef; title?: string; tags: readonly string[]; size: readonly [number, number, number]; variationId?: string }

const round = (value: number) => Math.round(value * 1e4) / 1e4;
/** The yaw that turns an item's front to `normal`: the parser's `itemYaw(theta + 180)` for a wall's own face. */
const facing = (normal: { x: number; z: number }) => Math.round(Math.atan2(-normal.x, normal.z) * 1e5) / 1e5;

/**
 * Where a curtain of `size` hangs over `opening` (`UpdateCurtainPosByOpening`): centred on it, its top at the
 * opening's top, its back 2.2 cm off the room's face of the wall, and cut off at the floor when it is longer than
 * the top is high.
 */
function hang(wall: Wall, opening: Opening, size: Item['size']): Pick<Item, 'position' | 'elevation' | 'rotation' | 'size' | 'wallId' | 'placement'> {
  const top = opening.sill + opening.height, height = Math.min(size.h, top);
  const reach = wall.thickness / 2 + CURTAIN_GAP + size.d / 2;
  return {
    placement: 'wall', wallId: wall.id, rotation: facing(wall.normalIn),
    position: { x: round(wall.start.x + wall.dir.x * opening.offset + wall.normalIn.x * reach), z: round(wall.start.z + wall.dir.z * opening.offset + wall.normalIn.z * reach) },
    elevation: round(top - height), size: { ...size, h: round(height) },
  };
}

/**
 * A curtain of `model` for `opening` (`ReplaceCurtains`: `UpdateCurtainSizeByOpening`, then
 * `UpdateCurtainPosByOpening`): drapes 1.2 times the opening's width, anything else its width, the model's own
 * height and depth. Null without the opening's wall or a size to scale.
 */
export function hungCurtain(level: Pick<Level, 'walls'>, opening: Opening, model: CurtainModel, id: string): Item | null {
  const wall = level.walls.find(value => value.id === opening.wallId);
  const [width, height, depth] = model.size;
  if (!wall || !(width > 0 && height > 0 && depth > 0)) return null;
  const wide = round(opening.width * (isUsualCurtain(model.tags) ? CURTAIN_WIDTH_FACTOR : 1));
  const placed = hang(wall, opening, { w: wide, h: height, d: depth });
  return {
    id, modelRef: { ...model.modelRef }, caption: model.title, tags: [...model.tags], ...placed,
    scale: [round(wide / width), round(placed.size.h / height), 1], mirrored: false, pivot: 'bottom',
    ...(model.variationId ? { variationId: model.variationId } : {}),
  };
}

/** The opening's design without the editor's own drawn curtains, which a catalog curtain or «Без штор» replaces. */
function withoutDrawnCurtains(design: OpeningDesign | undefined): OpeningDesign | undefined {
  if (!design?.curtains || design.curtains === 'none') return design;
  const { curtains: _curtains, ...rest } = design;
  return Object.keys(rest).length ? rest : undefined;
}

/**
 * The opening with `curtain` hanging over it in place of the curtains it had (`ReplaceCurtains.Value`); with
 * null, «Без штор»: its curtains go. Either way the curtains the editor used to draw for a window go too. The
 * level itself when there is nothing to change.
 */
export function withOpeningCurtains(level: Level, openingId: string, curtain: Item | null, tagsOf: TagsOf = knownTags): Level {
  const opening = level.openings.find(value => value.id === openingId);
  if (!opening) return level;
  const gone = new Set(openingCurtains(level, opening, tagsOf).map(item => item.id));
  const design = withoutDrawnCurtains(opening.design);
  if (!gone.size && !curtain && design === opening.design) return level;
  return {
    ...level,
    items: [...level.items.filter(item => !gone.has(item.id)), ...(curtain ? [curtain] : [])],
    openings: design === opening.design ? level.openings
      : level.openings.map(value => value.id !== openingId ? value : design ? { ...value, design } : (({ design: _design, ...rest }) => rest)(value)),
  };
}

/**
 * Curtains go with their opening. The app carries them through a drag and hangs them by the opening again as it
 * ends (`UpdateCurtainPosByOpening`, `WallSide.GetCurtainsOwnedBy` for another wall): an opening of `next` that
 * stands elsewhere than in `base` — along its wall, up or down, on another wall — takes the curtains it had in
 * `base`, centred on it and topped at its top, their width kept and their length cut at the floor — whatever its
 * kind, as a window from the cloud may not have its tags read yet. In a gesture
 * `base` is the level it started from, so every frame hangs them from where they were, not from the last frame.
 */
export function carryCurtains(base: Level, next: Level, tagsOf: TagsOf = knownTags): Level {
  let items: Item[] | null = null;
  for (const before of base.openings) {
    const after = next.openings.find(value => value.id === before.id);
    if (!after || (after.wallId === before.wallId && after.offset === before.offset && after.sill === before.sill && after.height === before.height)) continue;
    const wall = next.walls.find(value => value.id === after.wallId);
    if (!wall) continue;
    for (const curtain of openingCurtains(base, before, tagsOf)) {
      const list = items ?? next.items, index = list.findIndex(item => item.id === curtain.id);
      if (index < 0) continue;
      const placed = hang(wall, after, curtain.size);
      const scale: Item['scale'] = [curtain.scale[0], round(curtain.scale[1] * placed.size.h / curtain.size.h), curtain.scale[2]];
      items ??= next.items.slice();
      items[index] = { ...list[index], ...placed, scale };
    }
  }
  return items ? { ...next, items } : next;
}
